import React from "react";

/**
 * Reusable empty-state hero for pages where the agent hasn't created content yet.
 *
 * Props:
 *   icon        – React node (e.g. <FaChartArea />)
 *   title       – heading string
 *   description – paragraph or React node below the heading
 *   features    – optional array of { icon, text } bullet points
 *   examplePreview – optional React node rendered inside a faded "preview" card
 *   ctaLabel    – optional button label
 *   onCta       – optional click handler for the CTA
 *   isMobile    – layout flag
 */
export default function EmptyFeatureState({
  icon,
  title,
  description,
  features,
  examplePreview,
  ctaLabel,
  onCta,
  isMobile,
}) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      padding: isMobile ? 24 : 40,
      textAlign: "center",
      maxWidth: 720,
      margin: "0 auto 32px",
    }}>
      {/* Icon */}
      {icon && (
        <div style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
          fontSize: 24,
          color: "#2563eb",
        }}>
          {icon}
        </div>
      )}

      {/* Title */}
      <h2 style={{
        fontSize: isMobile ? 20 : 24,
        fontWeight: 700,
        color: "#1f2937",
        marginBottom: 12,
        marginTop: 0,
      }}>
        {title}
      </h2>

      {/* Description */}
      {description && (
        <p style={{
          fontSize: 15,
          color: "#6b7280",
          lineHeight: 1.7,
          marginBottom: features || examplePreview ? 24 : 0,
          maxWidth: 520,
          margin: features || examplePreview ? "0 auto 24px" : "0 auto",
        }}>
          {description}
        </p>
      )}

      {/* Feature bullets */}
      {features && features.length > 0 && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          textAlign: "left",
          maxWidth: 440,
          margin: "0 auto 28px",
        }}>
          {features.map((f, i) => (
            <div key={i} style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 13,
                color: "#6b7280",
                marginTop: 1,
              }}>
                {f.icon}
              </div>
              <span style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
                {f.text}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Example preview */}
      {examplePreview && (
        <div style={{
          position: "relative",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          marginBottom: ctaLabel ? 24 : 0,
        }}>
          <div style={{
            padding: 2,
            background: "#f9fafb",
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 12px",
              borderBottom: "1px solid #f3f4f6",
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#e5e7eb" }} />
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#e5e7eb" }} />
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#e5e7eb" }} />
              <span style={{ fontSize: 10, color: "#9ca3af", marginLeft: 8 }}>Example</span>
            </div>
            <div style={{ opacity: 0.7, pointerEvents: "none" }}>
              {examplePreview}
            </div>
          </div>
          {/* Fade overlay */}
          <div style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 60,
            background: "linear-gradient(transparent, #fff)",
            pointerEvents: "none",
          }} />
        </div>
      )}

      {/* CTA */}
      {ctaLabel && onCta && (
        <button
          onClick={onCta}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "12px 28px",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "#1d4ed8")}
          onMouseLeave={e => (e.currentTarget.style.background = "#2563eb")}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
